import { useCallback, useEffect, useState } from 'react'
import { useAuth } from 'lib/auth'
import { useCreateSubscriptionPaymentSession } from 'lib/graphql/mutations/create-subscription-payment-session'
import { MY_PGA_COM_HOST } from 'env'
import { amplitudeEvent } from 'lib/amplitude'
import useQueryString from 'lib/hooks/use-query-string'
import { useSnackbar, useSnackbarError } from 'lib/snackbar'
import { useHistory } from 'react-router-dom'

// Sources round-trip through the Stripe return URL, so anything a link can put
// in the query string is rejected unless it looks like a short slug.
const SOURCE_PATTERN = /^[a-z0-9-]{1,50}$/
const isValidSource = value =>
  typeof value === 'string' && SOURCE_PATTERN.test(value)

export const useUpgradeFlow = (
  pageName,
  { source, trackPromptShown = true } = {},
) => {
  const { user } = useAuth()
  const queryString = useQueryString()
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)

  const hasPaidTier = user?.coach?.hasPaidTierSubscription

  const firePromptShown = useCallback(() => {
    if (hasPaidTier) return
    amplitudeEvent('coach-plus-upgrade-prompt-shown', {
      path: window.location.pathname,
      page: pageName,
      ...(source ? { source } : {}),
    })
  }, [hasPaidTier, pageName, source])

  useEffect(() => {
    if (trackPromptShown) firePromptShown()
  }, [trackPromptShown, firePromptShown])

  // For pages that opt out of automatic tracking because the CTA only renders
  // conditionally. A no-op when the hook already tracks on mount.
  const trackPromptShownManually = useCallback(() => {
    if (!trackPromptShown) firePromptShown()
  }, [trackPromptShown, firePromptShown])

  useEffect(() => {
    if (queryString.subscription_success === 'true') {
      amplitudeEvent('coach-plus-upgrade-success', {
        path: window.location.pathname,
        page: pageName,
        ...(isValidSource(queryString.upgrade_source)
          ? { source: queryString.upgrade_source }
          : {}),
      })
      setShowSuccessModal(true)
    }
  }, [queryString.subscription_success, queryString.upgrade_source, pageName])

  const buildSuccessUrl = upgradeSource => {
    const base = `${MY_PGA_COM_HOST}${window.location.pathname}?subscription_success=true`
    return upgradeSource
      ? `${base}&upgrade_source=${encodeURIComponent(upgradeSource)}`
      : base
  }
  const [
    callCreateSubscriptionPaymentSession,
    { loading: upgradeLoading },
  ] = useCreateSubscriptionPaymentSession()

  const handleUpgradeClick = async options => {
    const clickSource = isValidSource(options?.source) ? options.source : source
    try {
      amplitudeEvent('coach-plus-upgrade-clicked', {
        path: window.location.pathname,
        page: pageName,
        ...(clickSource ? { source: clickSource } : {}),
      })
      const { data } = await callCreateSubscriptionPaymentSession({
        variables: { successUrl: buildSuccessUrl(clickSource) },
      })
      const {
        url,
        success,
        message,
      } = data?.createPgaCoachSubscriptionPaymentSession
      if (!success) {
        return snackbarError(message)
      }
      if (success && url) {
        window.location.href = url
      }
    } catch (e) {
      snackbarError(e?.message)
    }
  }

  const handleDismiss = () => {
    setShowSuccessModal(false)

    // Remove the subscription_success query parameter from URL
    const url = new URL(window.location.href)
    url.searchParams.delete('subscription_success')
    url.searchParams.delete('upgrade_source')
    history.replace(url.pathname + url.search)
  }

  return {
    hasPaidTier,
    trackPromptShown: trackPromptShownManually,
    showSuccessModal,
    upgradeLoading,
    handleUpgradeClick,
    handleDismiss,
  }
}
